import { useCallback, useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { HardDrives, PencilSimple, DownloadSimple } from '@phosphor-icons/react';
import { CoreInstanceType } from 'common/core/CoreInstanceConstants';
import { trpc, useMutation } from 'client/lib/trpc';
import { Button } from 'client/components/Button';
import { Dialog } from 'client/components/Dialog';
import { VMSelect } from './VMSelect';

const selectStyle = css`
  margin-top: 8px;
`;

export interface VMSelectDialogProps {
  open: boolean;
  deploymentID: number | null;
  coreInstanceTypeID: CoreInstanceType | null;
  onSave: (coreInstanceTypeID: CoreInstanceType) => void;
  onClose: () => void;
}

export const VMSelectDialog = ({ open, deploymentID, coreInstanceTypeID: initialValue, onSave, onClose }: VMSelectDialogProps) => {
  const theme = useTheme();
  const [coreInstanceTypeID, setCoreInstanceTypeID] = useState<CoreInstanceType>(initialValue || CoreInstanceType.SMALL);

  useEffect(() => {
    if (!open) return;
    setCoreInstanceTypeID(initialValue || CoreInstanceType.SMALL);
  }, [open]);

  const resizeMutation = useMutation(trpc.deployment.resize, {
    onSuccess: (data) => onSave(coreInstanceTypeID),
  });

  const handleResize = useCallback(() => {
    if (!deploymentID) return;
    resizeMutation.mutate({ deploymentID, coreInstanceTypeID });
  }, [deploymentID, coreInstanceTypeID]);

  return (
    <Dialog
      open={open}
      title="Change Deployment Size"
      description="Reconfigure the computing resources for this deployment"
      icon={<HardDrives color={theme.color.primary[100]} />}
      iconOrnament={<PencilSimple color={theme.color.primary[100]} />}
      dismissable={!resizeMutation.isLoading}
      actions={
        <>
          <Button disabled={resizeMutation.isLoading} onClick={onClose}>
            Cancel
          </Button>
          <Button primary loading={resizeMutation.isLoading} icon={<DownloadSimple weight="bold" />} onClick={handleResize}>
            Change Size
          </Button>
        </>
      }
      onClose={onClose}
    >
      <VMSelect value={coreInstanceTypeID} onChange={setCoreInstanceTypeID} css={selectStyle} />
    </Dialog>
  );
};
